import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import DateRange from '@/components/date/DateRange';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGetInvestorActivityQuery } from '@/store/usersApi';
import { Box, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts';
import { skipToken } from '@reduxjs/toolkit/query';
import dayjs from '@/config/dayjs';
import React, { useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { APP_DATE_FORMAT } from '@/constants/dateFormats';
import { parseOptionalDayjs } from '@/utils/date';
import { normalizeSearchText } from '@/utils/string';

interface PortalActivityProps {
  investorId: number;
}

const PortalActivity: React.FC<PortalActivityProps> = ({ investorId }) => {
  const { t } = useLocalization();
  const authLoaded = useAuthIsLoaded();

  const [startDate, setStartDate] = useState(
    dayjs().subtract(6, 'month').startOf('month').format(APP_DATE_FORMAT),
  );
  const [endDate, setEndDate] = useState(dayjs().format(APP_DATE_FORMAT));

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const { currentData, isLoading, isFetching, isError } =
    useGetInvestorActivityQuery(
      authLoaded && investorId ? { limit: 1000, investorId } : skipToken,
    );

  const records = useMemo(() => currentData?.Records ?? [], [currentData]);

  const dataSet = useMemo(() => {
    const start = dayjs(startDate, APP_DATE_FORMAT).startOf('day');
    const end = dayjs(endDate, APP_DATE_FORMAT).endOf('day');

    const loginRecords = records.filter((r) => {
      const d = parseOptionalDayjs(r.AuditDate);
      return (
        d != null &&
        d.isAfter(start.subtract(1, 'millisecond')) &&
        d.isBefore(end.add(1, 'millisecond')) &&
        normalizeSearchText(r.Action).includes('login')
      );
    });

    const byMonth: Record<
      string,
      { desktop: number; mobile: number; other: number }
    > = {};
    loginRecords.forEach((r) => {
      const d = parseOptionalDayjs(r.AuditDate);
      if (!d) return;
      const monthKey = d.endOf('month').format('YYYY-MM-DD');
      const action = normalizeSearchText(r.Action);
      const isMobile = action.includes('mobile');
      const isDesktop = action.includes('desktop') || action.includes('web');
      const device: 'desktop' | 'mobile' | 'other' = isMobile
        ? 'mobile'
        : isDesktop
          ? 'desktop'
          : 'other';
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!byMonth[monthKey])
        byMonth[monthKey] = { desktop: 0, mobile: 0, other: 0 };
      byMonth[monthKey][device] += 1;
    });

    const rows = Object.entries(byMonth)
      .map(([monthStr, counts]) => ({
        date: dayjs(monthStr).toDate(),
        desktop: counts.desktop,
        mobile: counts.mobile,
        other: counts.other,
        total: counts.desktop + counts.mobile + counts.other,
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return rows;
  }, [records, startDate, endDate]);

  const showLoadingSpinner =
    isLoading || (isFetching && !currentData) || !authLoaded;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant='h6'>
          {t('portalActivity', 'Portal Activity')}
        </Typography>
        <Box>
          <DateRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
          />
        </Box>
      </Box>

      {showLoadingSpinner ? (
        <LoadingSpinner />
      ) : isError ? (
        <Typography color='error'>
          {t('errorLoadingData', 'Error loading data.')}
        </Typography>
      ) : (
        <Box sx={{ py: 1 }}>
          <Typography
            variant='subtitle1'
            sx={{ mb: 1 }}
          >
            {t('totalLoginsByMonth', 'Total Logins by Month')}
          </Typography>
          {dataSet.length > 0 ? (
            <LineChart
              height={330}
              dataset={dataSet}
              series={[
                {
                  dataKey: 'total',
                  showMark: true,
                  area: true,
                  color: 'rgba(116, 139, 155, 1)',
                  label: t('total', 'Total'),
                },
                {
                  dataKey: 'desktop',
                  label: t('desktop', 'Desktop'),
                  color: '#6a8caf',
                  showMark: false,
                },
                {
                  dataKey: 'mobile',
                  label: t('mobile', 'Mobile'),
                  color: '#90caf9',
                  showMark: false,
                },
              ]}
              xAxis={[
                {
                  dataKey: 'date',
                  scaleType: 'time',
                  tickMinStep: 3600 * 1000 * 24 * 28,
                },
              ]}
              sx={{
                '& .MuiAreaElement-root': {
                  fillOpacity: 0.5,
                },
              }}
            />
          ) : (
            <Box
              sx={{
                height: 330,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography>
                {t('noActivityDataAvailable', 'No activity data available')}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(PortalActivity);
